"use client";

import { offstreetClient } from "@offstreetparking/portal-flow/api";
import { CompanyStateProvider } from "@offstreetparking/portal-flow/providers/company";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { RouterOutputs } from "~/services/offstreetApi/types";
import { fetchCompanyBySubdomain } from "./fetchCompanyBySubdomain";

type Company = Omit<NonNullable<Awaited<ReturnType<typeof fetchCompanyBySubdomain>>>, "isFromSubdomain">;

type CompanyContextValue = {
	company?: Company;
	isPending: boolean;
	error?: Error;
	isFromSubdomain: boolean;
	setCompany: (company: PageCompany) => void;
	resetCompany: () => void;
};

const CompanyContext = createContext<CompanyContextValue | undefined>(undefined);

export const useCompany = () => {
	const context = useContext(CompanyContext);
	if (context === undefined) {
		throw new Error("useCompany must be used within a CompanyProvider");
	}
	return context;
};

type CompanyProviderProps = {
	children: React.ReactNode;
	company: Awaited<ReturnType<typeof fetchCompanyBySubdomain>>;
	isFromSubdomain: boolean;
	locale?: string;
};

const CompanyInnerProvider = ({
	children,
	company: initialData,
	isFromSubdomain: initialIsFromSubdomain,
	locale,
}: CompanyProviderProps) => {
	const [pageCompany, setPageCompany] = useState<PageCompany>();
	const {
		data: subdomainCompany,
		isPending,
		error,
	} = useQuery({
		queryKey: ["company.fetchCompanyBySubdomain", locale, pageCompany?.subdomain],
		queryFn: () => fetchCompanyBySubdomain({ locale, subdomain: pageCompany?.subdomain ?? undefined }),
		initialData,
	});
	const isFromSubdomain = initialIsFromSubdomain || !!subdomainCompany?.isFromSubdomain;
	const setCompany = useCallback(
		(company: PageCompany) => {
			if (isFromSubdomain) {
				return;
			}
			setPageCompany(company);
		},
		[isFromSubdomain],
	);
	const resetCompany = useCallback(() => {
		setPageCompany(undefined);
	}, []);
	const company = subdomainCompany ?? undefined;
	const queryClient = useQueryClient();
	const flagsCompanyId = useRef(company?.id);
	useEffect(() => {
		if (isFromSubdomain || flagsCompanyId.current === company?.id) {
			return;
		}
		flagsCompanyId.current = company?.id;
		void queryClient.invalidateQueries({ queryKey: getQueryKey(offstreetClient.featureFlags.getFeatureFlags) });
	}, [isFromSubdomain, company?.id, queryClient]);
	return (
		<CompanyContext.Provider
			value={{
				company,
				isPending,
				error: error ?? undefined,
				isFromSubdomain,
				setCompany,
				resetCompany,
			}}
		>
			<CompanyStateProvider value={{ company, isPending, error: error ?? undefined, isFromSubdomain }}>
				{children}
			</CompanyStateProvider>
		</CompanyContext.Provider>
	);
};

export const CompanyProvider = ({ children, company, isFromSubdomain, locale }: CompanyProviderProps) => {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
						retry: 2,
					},
				},
			}),
	);
	return (
		<QueryClientProvider client={queryClient}>
			<CompanyInnerProvider company={company} isFromSubdomain={isFromSubdomain} locale={locale}>
				{children}
			</CompanyInnerProvider>
		</QueryClientProvider>
	);
};

export const CompanyScope = ({ company }: { company: { id: number; subdomain: string | null } }) => {
	const { isFromSubdomain, setCompany, resetCompany } = useCompany();
	const isSharedPortal = !isFromSubdomain;
	useEffect(() => {
		if (!isSharedPortal) {
			return;
		}
		setCompany({ id: company.id, subdomain: company.subdomain });
		return resetCompany;
	}, [isSharedPortal, company.id, company.subdomain, setCompany, resetCompany]);
	return null;
};

export const PageCompanyProvider = ({
	children,
	company,
	featureFlags,
}: {
	children: React.ReactNode;
	company: Company;
	featureFlags: RouterOutputs["featureFlags"]["getFeatureFlags"] | undefined;
}) => {
	const context = useCompany();
	const utils = offstreetClient.useUtils();
	useState(() => {
		if (featureFlags) {
			utils.featureFlags.getFeatureFlags.setData(undefined, featureFlags);
		}
	});
	const value = { ...context, company };
	return (
		<>
			<CompanyScope company={company} />
			<CompanyContext.Provider value={value}>
				<CompanyStateProvider value={value}>{children}</CompanyStateProvider>
			</CompanyContext.Provider>
		</>
	);
};

type PageCompany = Pick<Company, "id" | "subdomain">;
